import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';

export interface Diagnostic {
  diagnostic: {
    sales_order: SalesOrder;
    sales_order_item: SalesOrderItem;
    sales_order_appointment: SalesOrderAppointment;
    person: Person;
    product: Product;
    service: Service;
    address?: Address;
    person_identity?: PersonIdentity;
  };
}
interface SalesOrder {
  sales_order_id: number;
  sales_order_item_id: number;
  sales_order_external_id: number;
  datetime_create: Date;
  customer_person_id: number;
  business_person_id: number;
  items_subtotal?: number;
  amount?: number;
  vat?: number;
  currency?: string;
  payment_method_id?: number;
  person_id_create?: number;
  ta_id?: number;
  transaction_id?: number;
  paid?: string;
  payment_method?: string;
}

interface SalesOrderItem {
  sales_order_id: number;
  product_id: number;
  unit_of_measure_id: any;
  quantity: any;
  currency: string;
  amount: any;
  sales_order_item_id: number;
  external_sales_order_item_id: number;
  datetime_created: any;
  with_chatting: any;
  datetime_cancel: any;
  person_cancel: any;
  record_state: string;
  price: number;
}
interface SalesOrderAppointment {
  sales_order_appointment_id: number;
  sales_order_id: number;
  start?: Date;
  end_time?: Date;
  business_partner_timezone_id?: number;
  record_state?: string;
  valid_from: Date;
  invalid_from: Date;
  status?: string;
}
interface Person {
  person_id: number;
  person_tittle: number;
  person_firstname: string;
  person_lastname: string;
  person_gender_id: number;
  person_birthdate?: Date;
  person_email: string;
  person_account_number: string;
  person_mobile_phone: string;
  person_landline_phone: string;
  person_nationality_id: number;
  person_category_id: number;
}
interface Product {
  product_id: number;
  product_name: string;
  product_long_description: string;
}
interface Service {
  service_id: number;
  service_name: string;
}
interface Address {
  address_id: number;
  street: string;
  house_number: string;
  city: string;
  postcode: number;
}
interface PersonIdentity {
  person_identity_id: number;
  person_id: number;
  person_identity_type_id: number;
  person_identity_number: string;
}

export interface CheckInState {
  scanCustomerStatus: string;
  companions: Person[];
  diagnostics: Diagnostic[];
  orderStates: any;
}

const initialState: CheckInState = {
  scanCustomerStatus: 'idle',
  companions: [],
  diagnostics: [],
  orderStates: [],
};

type SearchCheckInBody = {
  patientEmail?: string;
  accountNumber?: string;
};
type SalesOrderStatesBody = {
  personAccount?: string;
  accountNumber?: string;
};

export const searchCheckIn = createAsyncThunk(
  'checkIn/search',
  async (searchCheckinBody: SearchCheckInBody, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const diagnosticResponse: any = await client.post(
        `/healthpoint/search-check-in/${state?.auth?.accountInfo?.account_number}`,
        {
          patientEmail: searchCheckinBody.patientEmail,
          accountNumber: searchCheckinBody.accountNumber,
        }
      );
      return diagnosticResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);
export const salesOrderStates = createAsyncThunk(
  'checkIn/salesOrderStates',
  async (salesOrderStates: SalesOrderStatesBody, { dispatch, rejectWithValue, getState }) => {
    try {
      const salesOrderStatesResponse: any = await client.get(
        `/core/merchant/${salesOrderStates.accountNumber}/product_orders?participant_filter=${salesOrderStates.personAccount}`
      );
      return salesOrderStatesResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const checkInSlice = createSlice({
  name: 'checkIn',
  initialState,
  reducers: {
    resetCheckIn: (_) => {
      return initialState;
    },
    resetScanCustomerStatus: (state) => {
      return {
        ...state,
        scanCustomerStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchCheckIn.pending, (state) => {
        state.scanCustomerStatus = 'pending';
      })
      .addCase(searchCheckIn.fulfilled, (state, action) => {
        state.diagnostics = action.payload.data.diagnostics;
        state.scanCustomerStatus = 'fulfilled';
      })
      .addCase(salesOrderStates.fulfilled, (state, action) => {
        state.orderStates = action.payload.data;
      })
      .addCase(searchCheckIn.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        if (Array.isArray(action?.payload?.error)) {
          state.companions = action?.payload?.error;
        } else {
          state.companions = [];
        }
        state.scanCustomerStatus = 'rejected';
      });
  },
});

export const { resetCheckIn, resetScanCustomerStatus } = checkInSlice.actions;

export default checkInSlice.reducer;

export const selectDiagnostics = (state: RootState) => state.checkIn.diagnostics;
export const selectScanCustomerCompanions = (state: RootState) => state.checkIn.companions;
export const selectScanCustomerStatus = (state: RootState) => state.checkIn.scanCustomerStatus;
export const selectSalesOrderStatus = (state: RootState) => state.checkIn.orderStates;
